<script setup lang="ts">
	import { useLocalStorage, useScroll } from '@vueuse/core'
	import type { ChannelsStorage } from '~/types'
	import StringMethods from '~/utils/character'

	const scrollUp = shallowRef(false)
	const { y } = useScroll(import.meta.client ? document : null)
	const { seeMoreProducts } = useProductsStore()
	const { toLower } = StringMethods()
	const cookiePolicy = useLocalStorage<boolean>('show_cookie_banner', false)
	const showCookie = shallowRef(false)
	const comingFromDiaspora = shallowRef(false)

	const source = computed(() => toLower(String(useRoute().query?.utm_source) ?? '') as ChannelSource)

	const scrollToTop = () => {
		try {
			if (window) {
				window.scrollTo({
					top: 0,
					behavior: 'smooth'
				})
			}
		} catch (e) {}
	}

	watch(y, val => {
		const documentHeight = Math.round(document.body.offsetHeight / 2)
		scrollUp.value = false
		if (val > documentHeight) {
			scrollUp.value = true
		}
	})

	onMounted(async () => {
		if (history && history.state?.back && String(history.state.back).includes('sendcare')) {
			comingFromDiaspora.value = true
		}

		if (import.meta.client && 'localStorage' in window) {
			// Check if channel exists
			const channels = localStorage.getItem('channels')
			let chanelObject: ChannelsStorage | null = null
			if (channels) {
				chanelObject = JSON.parse(channels)
			}

			// Check if the source has expired and clear
			const date = new Date()
			if (chanelObject && date.getDate() === Number(chanelObject.time_stamp)) {
				localStorage.removeItem('channels')
				chanelObject = null
			}
			const { marketingChannels } = useStore()
			// Set Google utm tags
			if (marketingChannels.includes(source.value) && !channels) {
				const setDate = new Date()
				setDate.setDate(date.getDate() + 7)

				localStorage.setItem(
					'channels',
					JSON.stringify({
						time_stamp: setDate.getDate(),
						utm_source: source.value,
						date_created: chanelObject?.date_created ?? date.getDate()
					})
				)
			}
		}
		await Timeout()
		showCookie.value = true
	})
</script>
<template>
	<div>
		<header-nav v-if="!comingFromDiaspora" />

		<div>
			<nuxt-page />

			<transition
				enter-active-class="animate__animated animate__animate-faster-x2 animate__bounceIn"
				leave-active-class="animate__animated animate__animate-faster-x2 animate__zoomOut"
			>
				<button
					v-if="scrollUp"
					type="button"
					aria-label="Scroll to top"
					class="fixed bottom-20 right-5 sm:right-4 custom-transition-3 z-[800] justify-center md:h-[56px] md:w-[56px] w-[40px] h-[40px] bg-health-100 flexed rounded-full default-shadow"
					@click="scrollToTop"
				>
					<span class="-rotate-90">
						<tools-icon dir="arrows" icon="arrow-short-r" path-class="fill-gray-700" />
					</span>
				</button>
			</transition>
		</div>
		<main-product-list-dialog v-model:show="seeMoreProducts.isOpen" :category="seeMoreProducts.productCateory" />

		<transition
			name="transition"
			enter-active-class="animate__animated animate__animate-faster-x2 animate__zoomIn"
			leave-active-class="animate__animated animate__animate-faster-x2 animate__zoomOut"
		>
			<div
				v-if="!cookiePolicy && showCookie"
				class="fixed bottom-0 flex items-center custom-transition-2 flex-wrap justify-around left-0 right-0 z-[9999] bg-white default-shadow py-5 px-4 gap-3 sm:gap-5"
			>
				<div class="sm:text-center sm:flex-1 text-sm text-gray-500 font-medium">
					We use cookies to ensure you get the best browsing experience. By continuing to use our site, you
					agree to our use of cookies.
				</div>

				<div class="text-sm flex-none inline-flex items-center justify-end w-full sm:w-auto">
					<div class="inline-flex gap-5 items-center">
						<nuxt-link to="/privacy-policy" class="font-semibold flex-none"> Privacy Policy </nuxt-link>
						<ui-button
							class="p-0 text-main flex-none h-auto w-auto"
							variant="default"
							@click="cookiePolicy = true"
						>
							OK
						</ui-button>
					</div>
				</div>
			</div>
		</transition>
		<footer-base v-if="!comingFromDiaspora" />
		<sendcare-footer variant="" v-else />
	</div>
</template>
